import dayjs from "dayjs";
import {
  type LoaderFunction,
  Navigate,
  redirect,
  useLoaderData,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import { useTranslation } from "@/core/i18n";
import { getGetMembershipsQueryOptions } from "@/api/orval/membership/membership";
import {
  getGetMembershipChangeOptionsQueryOptions,
  getGetMyMembershipsQueryOptions,
} from "@/api/orval/platform-user-membership/platform-user-membership";
import { Button } from "atoms/button/Button";
import { Icon } from "atoms/icon/Icon";
import ChevronLeft from "atoms/icon/icons/chevron_left.svg";
import Check from "atoms/icon/icons/ic_check_xsmall_ol.svg";
import Snowflake from "atoms/icon/icons/ic_snowflake_small_ol.svg";
import SwapHorizontal from "atoms/icon/icons/ic_swap_horizontal.svg";
import { TextButton } from "atoms/text-button/TextButton";
import { usePlatform } from "hooks/usePlatform";
import TopNav from "molecules/navbars/TopNav";
import { queryClient } from "pages/Root";
import { formatBillingPeriodInterval, parseBillingPeriodName } from "utils/billingPeriod.helpers";

import {
  canOfferFreeze,
  canOfferPlanChange,
  MEMBERSHIP_CHANGE_DIRECTION_DOWNGRADE,
  type MembershipCancellationLocationState,
  type MembershipChangeLocationState,
} from "./membershipHelper";

const STEP_PARAM = "step";

type SaveOfferStep = "downgrade" | "freeze";

const load = async (platformUserMembershipId: string) => {
  const userMemberships = await queryClient.fetchQuery(getGetMyMembershipsQueryOptions());
  const membership = userMemberships.find(
    (m) => m.platformUserMembershipId === platformUserMembershipId,
  );

  if (!membership) {
    return redirect("/wallet/memberships/manage");
  }

  const [changeOptions, catalog] = canOfferPlanChange(membership)
    ? await Promise.all([
        queryClient.fetchQuery(getGetMembershipChangeOptionsQueryOptions(platformUserMembershipId)),
        // Change options carry no benefits; the location's membership catalog does.
        queryClient.fetchQuery(getGetMembershipsQueryOptions()),
      ])
    : [null, []];

  // Nearest tier first: the smallest step down is the likeliest save.
  const downgrades = (changeOptions?.options ?? [])
    .filter(
      ({ direction, eligible }) => eligible && direction === MEMBERSHIP_CHANGE_DIRECTION_DOWNGRADE,
    )
    .sort((a, b) => b.billingPeriodCost - a.billingPeriodCost)
    .map((option) => ({
      ...option,
      // A plan missing from the catalog (e.g. unlisted) just shows no benefits.
      benefits:
        catalog
          .find(({ id }) => id === option.membershipId)
          ?.membershipBenefits.map(({ description }) => description) ?? [],
    }));

  const steps: SaveOfferStep[] = [
    ...(downgrades.length > 0 ? (["downgrade"] as const) : []),
    ...(canOfferFreeze(membership) ? (["freeze"] as const) : []),
  ];

  return { platformUserMembershipId, membership, downgrades, steps };
};

type LoaderData = Exclude<Awaited<ReturnType<typeof load>>, Response>;
export const walletMembershipCancelOffersLoader: LoaderFunction = ({
  params: { platformUserMembershipId },
}) => {
  if (!platformUserMembershipId) {
    return redirect("..");
  }

  return load(platformUserMembershipId);
};

/**
 * Save offers between the reason page and the final confirm, one per screen: a lower plan, then a
 * freeze. Every screen keeps a prominent way to continue cancelling (California's auto-renewal
 * law requires the cancel option to stay visible next to any retention offer). Steps live in the
 * URL so the device back button walks back through them.
 */
export const WalletMembershipCancelOffersPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { platform } = usePlatform();
  const [searchParams, setSearchParams] = useSearchParams();
  const { platformUserMembershipId, membership, downgrades, steps } = useLoaderData() as LoaderData;
  const { membershipCancellationReasonId } = (useLocation().state ??
    {}) as MembershipCancellationLocationState;

  const manageBasePath = `/wallet/memberships/manage/${platformUserMembershipId}`;
  const cancellationState = {
    membershipCancellationReasonId,
  } satisfies MembershipCancellationLocationState;

  if (!membershipCancellationReasonId) {
    return <Navigate replace to={manageBasePath} />;
  }
  if (steps.length === 0) {
    return <Navigate replace to={`${manageBasePath}/cancel`} state={cancellationState} />;
  }

  const requestedStep = Number(searchParams.get(STEP_PARAM));
  const stepIndex =
    Number.isInteger(requestedStep) && requestedStep >= 0
      ? Math.min(requestedStep, steps.length - 1)
      : 0;
  const step = steps[stepIndex];
  const renewalDate = dayjs(membership.expiration).format("MMM D, YYYY");
  const current = membership.membership;

  const continueToCancel = () => {
    if (stepIndex < steps.length - 1) {
      // Router state doesn't survive setSearchParams, so carry the reason forward explicitly.
      setSearchParams({ [STEP_PARAM]: `${stepIndex + 1}` }, { state: cancellationState });
      return;
    }
    navigate(`${manageBasePath}/cancel`, { state: cancellationState });
  };

  const formatPrice = (cost: number, interval: number, intervalCount: number) =>
    formatBillingPeriodInterval(cost, parseBillingPeriodName(interval), intervalCount);
  // Only comparable when both plans bill on the same cadence.
  const savingsLabel = (option: LoaderData["downgrades"][number]) =>
    option.billingPeriodInterval === current.billingPeriodInterval &&
    option.billingPeriodIntervalCount === current.billingPeriodIntervalCount &&
    option.billingPeriodCost < current.billingPeriodCost
      ? t("walletMembershipCancelOffersPage.saveAmount", {
          price: formatPrice(
            current.billingPeriodCost - option.billingPeriodCost,
            option.billingPeriodInterval,
            option.billingPeriodIntervalCount,
          ),
        })
      : null;

  const freezePoints = [
    t("walletMembershipCancelOffersPage.freezePointNoCharges"),
    t("walletMembershipCancelOffersPage.freezePointKeepPlan", { membershipName: current.name }),
    t("walletMembershipCancelOffersPage.freezePointResumes"),
    platform?.name
      ? t("walletMembershipCancelOffersPage.freezePointReviewed", { platformName: platform.name })
      : t("walletMembershipCancelOffersPage.freezePointReviewedNoPlatform"),
  ];

  return (
    <div className="relative flex h-full flex-col bg-one">
      <TopNav
        startIconSVG={ChevronLeft}
        showHeaderItems={false}
        onStartIconClick={() => navigate(-1)}
      >
        <div className="flex justify-center text-sub2">
          {t("walletMembershipCancelOffersPage.header")}
        </div>
      </TopNav>

      <div className="flex grow flex-col gap-y-5 px-5 pb-6 pt-4 text-left">
        {step === "downgrade" && (
          <>
            <div className="flex flex-col gap-y-2">
              <h1 className="text-title">{t("walletMembershipCancelOffersPage.downgradeTitle")}</h1>
              <span className="text-body2 text-secondary">
                {t("walletMembershipCancelOffersPage.downgradeBody", { date: renewalDate })}
              </span>
            </div>
            <div className="flex flex-col gap-y-4">
              {downgrades.map((option) => {
                const savings = savingsLabel(option);
                return (
                  <div
                    key={option.membershipId}
                    className="flex flex-col gap-y-4 rounded-2xl bg-white p-5 shadow-[0px_2px_10px_rgba(0,0,0,0.06)]"
                  >
                    <div className="flex items-start justify-between gap-x-3">
                      <div className="flex flex-col gap-y-1">
                        <span className="text-bold1 text-primary">{option.name}</span>
                        <span className="text-title text-primary">
                          {formatPrice(
                            option.billingPeriodCost,
                            option.billingPeriodInterval,
                            option.billingPeriodIntervalCount,
                          )}
                        </span>
                      </div>
                      {savings && (
                        <span className="shrink-0 rounded-full bg-brand-color px-3 py-1 text-body3 font-semibold text-white">
                          {savings}
                        </span>
                      )}
                    </div>
                    {option.benefits.length > 0 && (
                      <ul className="flex flex-col gap-y-2 border-t border-black/5 pt-4">
                        {option.benefits.map((benefit) => (
                          <li
                            key={benefit}
                            className="flex items-start gap-x-2 text-body2 text-primary"
                          >
                            <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-brand-color/10">
                              <Icon svg={Check} color="brandColor" size="x3small" />
                            </span>
                            {benefit}
                          </li>
                        ))}
                      </ul>
                    )}
                    <Button
                      fullWidth
                      startIconSVG={SwapHorizontal}
                      onClick={() =>
                        navigate(`${manageBasePath}/change`, {
                          state: {
                            preselectedMembershipId: option.membershipId,
                          } satisfies MembershipChangeLocationState,
                        })
                      }
                    >
                      {t("walletMembershipCancelOffersPage.switchTo", { planName: option.name })}
                    </Button>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {step === "freeze" && (
          <div className="flex flex-col gap-y-5 rounded-2xl bg-white p-5 shadow-[0px_2px_10px_rgba(0,0,0,0.06)]">
            <div className="flex flex-col items-center gap-y-3 pt-2 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-brand-color/10">
                <Icon svg={Snowflake} color="brandColor" size="large" />
              </div>
              <h1 className="text-title">{t("walletMembershipCancelOffersPage.freezeTitle")}</h1>
              <span className="text-body2 text-secondary">
                {t("walletMembershipCancelOffersPage.freezeBody", {
                  membershipName: current.name,
                })}
              </span>
            </div>
            <ul className="flex flex-col gap-y-3 rounded-xl bg-one p-4">
              {freezePoints.map((point) => (
                <li key={point} className="flex items-start gap-x-2 text-body2 text-primary">
                  <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-brand-color/10">
                    <Icon svg={Check} color="brandColor" size="x3small" />
                  </span>
                  {point}
                </li>
              ))}
            </ul>
            <Button
              fullWidth
              startIconSVG={Snowflake}
              onClick={() => navigate(`${manageBasePath}/freeze`)}
            >
              {t("walletMembershipCancelOffersPage.freezeButton")}
            </Button>
          </div>
        )}

        <div className="grow" />
        <div className="flex flex-col items-center gap-y-3">
          <Button fullWidth style="danger" onClick={continueToCancel}>
            {t("walletMembershipCancelOffersPage.noThanks")}
          </Button>
          <TextButton
            variant="secondary"
            className="text-body2"
            onClick={() => navigate("/wallet/memberships/manage")}
          >
            {t("walletMembershipCancelOffersPage.keepMembership")}
          </TextButton>
        </div>
      </div>
    </div>
  );
};
